import {useMemo} from "react";
import {type QueryClient, useQueryClient} from "@tanstack/react-query";
import {isAxiosError} from "axios";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {planUrl, type PlanAction} from "../services/categoryProfileUrls";
import type {CategoryPlan, PlanContent, PlanHistoryEntry, PlanSectionKey} from "../types/strategyTypes";
import {done, failed} from "./notices";

export function useCategoryPlanQuery(databagId: number, path: string) {
    const url = planUrl(databagId, path);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<CategoryPlan>(url)).data,
        enabled: databagId > 0 && Boolean(path),
        retry: false,
    });
}

/** Every change to the plan, newest first (audit log). Loaded when `enabled`, fresh each time. */
export function usePlanHistoryQuery(databagId: number, path: string, enabled: boolean) {
    const url = planUrl(databagId, path, 'history');
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<PlanHistoryEntry[]>(url)).data,
        enabled: enabled && databagId > 0 && Boolean(path),
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
}

/**
 * Writes run one after the other and each reads the latest `version` from the cache when it is sent, so quick
 * successive edits (a drag, then a rename) never refuse each other. A 409 means someone else changed the plan:
 * the plan is reloaded and the user is told.
 */
const queues = new Map<string, Promise<unknown>>();

function enqueue<T>(key: string, job: () => Promise<T>): Promise<T> {
    const next = (queues.get(key) ?? Promise.resolve()).catch(() => undefined).then(job);
    queues.set(key, next);
    return next;
}

async function send(qc: QueryClient, key: string, method: 'patch' | 'post', url: string,
    body: (version: number) => object): Promise<CategoryPlan> {
    return enqueue(key, async () => {
        const version = qc.getQueryData<CategoryPlan>([key])?.version ?? 0;
        try {
            const {data} = await queryHttp.request<CategoryPlan>({method, url, data: body(version)});
            qc.setQueryData([key], data);
            return data;
        } catch (e) {
            if (isAxiosError(e) && e.response?.status === 409) void qc.invalidateQueries({queryKey: [key]});
            throw e;
        }
    });
}

/** Every plan write, with its notice and Undo. Section edits show at once (optimistic) and settle on the answer. */
export function usePlanActions(databagId: number, path: string) {
    const qc = useQueryClient();
    return useMemo(() => {
        const key = planUrl(databagId, path);
        const url = (a: PlanAction) => planUrl(databagId, path, a);
        const current = () => qc.getQueryData<CategoryPlan>([key]);

        const writeSection = <K extends PlanSectionKey>(section: K, value: PlanContent[K]) => {
            const plan = current();
            if (plan) qc.setQueryData<CategoryPlan>([key], {...plan, content: {...plan.content, [section]: value}});
            return send(qc, key, 'patch', url(''), version => ({section, value, version}))
                .catch(e => {
                    void qc.invalidateQueries({queryKey: [key]});
                    throw e;
                });
        };

        return {
            /** Replace one section. `message` names the change in the notice; Undo writes the previous value back. */
            save: async <K extends PlanSectionKey>(section: K, value: PlanContent[K], message = 'Plan updated.') => {
                const previous = current()?.content[section];
                try {
                    const plan = await writeSection(section, value);
                    const reopened = plan.events[0]?.kind === 'reopened' && current()?.status === 'draft';
                    done(reopened ? `${message} The plan needs a new sign-off.` : message,
                        previous === undefined ? undefined : () => writeSection(section, previous));
                } catch (e) {
                    failed(e);
                }
            },

            submit: async (note: string) => {
                try {
                    await send(qc, key, 'post', url('submit'), version => ({note, version}));
                    done('Submitted for sign-off.', () => send(qc, key, 'post', url('withdraw'), version => ({version})));
                } catch (e) {
                    failed(e);
                    throw e;
                }
            },

            withdraw: async () => {
                try {
                    await send(qc, key, 'post', url('withdraw'), version => ({version}));
                    done('Submission withdrawn.');
                } catch (e) {
                    failed(e);
                }
            },

            decide: async (slot: string, decision: 'approved' | 'returned', comment: string) => {
                try {
                    await send(qc, key, 'post', url('decide'), version => ({slot, decision, comment, version}));
                    done(decision === 'approved' ? 'Signed off.' : 'Returned with comments.');
                } catch (e) {
                    failed(e);
                    throw e;
                }
            },
        };
    }, [qc, databagId, path]);
}

export type PlanActions = ReturnType<typeof usePlanActions>;
